/** @jsxImportSource @emotion/react */
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from 'qs';
import SmartLoginForm from 'components/Login/SmartLoginForm';
import SSOSignedInBanner, {
  useShouldShowSSOSignedInBanner,
} from 'components/Login/SSOSignedInBanner';
import { doPreLoginCheck } from 'api-client/accounts';
import type { AccountValidationType } from 'api-client/accounts';
import { LoginProviderEnum } from 'bc-model';
import { ODSSpinner, ODSLoginPortal } from 'bc-ods';
import SmartLoginContextProvider from './context/SmartLoginContextProvider';
import { includeBasePath } from 'bc-artifacts-client/utils';

function LoginPortalWithSSOBanner({ children }: { children: ReactNode }): ReactElement {
  const shouldShowSSOBanner = useShouldShowSSOSignedInBanner();
  return (
    <ODSLoginPortal topBanner={shouldShowSSOBanner ? <SSOSignedInBanner /> : undefined}>
      {children}
    </ODSLoginPortal>
  );
}

const parseBoolean = (b: string | undefined): boolean => b?.toLowerCase() === 'true';
const cleanupAccountValidation = (accountValidation: any): AccountValidationType => {
  return {
    ...accountValidation,
    mustRegister: parseBoolean(accountValidation.mustRegister),
    hasUnlinkedProfile: parseBoolean(accountValidation.hasUnlinkedProfile),
  };
};

const oidcProfileToAccountValidation = (oidcProfile: any): AccountValidationType | undefined => {
  if (!oidcProfile) {
    return undefined;
  }

  if (typeof oidcProfile === 'string') {
    return cleanupAccountValidation(JSON.parse(decodeURIComponent(oidcProfile)));
  }

  try {
    const accountValidation: AccountValidationType = { ...oidcProfile };
    return cleanupAccountValidation(accountValidation);
  } catch (err) {
    console.error('Unable to parse oidcProfile', err);
  }

  return undefined;
};

function Login(): ReactElement {
  const [allowedSchemas, setAllowedSchemas] = useState<LoginProviderEnum[]>([]);
  const [showCuiDisclaimer, setShowCuiDisclaimer] = useState<boolean>(false);
  const [accountLinkingEnabled, setAccountLinkingEnabled] = useState<boolean>(false);
  const [preLoginLoading, setPreLoginLoading] = useState<boolean>(true);
  const [accountValidation, setAccountValidation] = useState<AccountValidationType>();
  const { state: rawState, search: queryString } = useLocation();
  const { from = '' } = (rawState as { from?: { pathname: string; search: string } }) ?? {};

  const {
    next: nextParam,
    profile = 'null',
    oidcProfile,
  } = parse(queryString, { ignoreQueryPrefix: true });

  const passedInProfile = JSON.parse(profile as string);

  useEffect(() => {
    void (async function getSchemas() {
      const preLogin = await doPreLoginCheck();

      const {
        schemas,
        cuiDisclaimer,
        accountLinking,
        authenticated,
        redirectUrl,
        accountValidation: preLoginAccountValidation,
      } = preLogin.data;

      if (authenticated) {
        window.location.href = redirectUrl!;
        return;
      }

      // Moved spinner clear after early exit, to prevent flashing the access denied screen to the
      // user.
      setPreLoginLoading(false);

      setAllowedSchemas(schemas);
      setShowCuiDisclaimer(cuiDisclaimer);
      setAccountLinkingEnabled(accountLinking);

      const bypassLoginScreen = schemas?.includes(LoginProviderEnum.JWT);

      if (bypassLoginScreen && preLoginAccountValidation?.userDetails) {
        setAccountValidation(preLoginAccountValidation);
      }
    })();
  }, []);

  const authStrategy =
    allowedSchemas.includes(LoginProviderEnum.OIDC) && !oidcProfile ? 'oidc' : 'login';
  const authEndpoint = includeBasePath(`/api/auth/${authStrategy}`);

  let next = nextParam as string;

  if (from) {
    const { pathname, search } = from;
    next = [pathname, search].join('');
  }

  const authQueryString = next ? `?next=${encodeURIComponent(next)}` : '';
  const authUrl = `${authEndpoint}${authQueryString}`;

  if (authStrategy !== 'login') {
    window.location.href = encodeURI(authUrl);
    return <div />;
  }

  if (preLoginLoading) {
    return (
      <ODSLoginPortal>
        <div
          className="d-flex align-items-center justify-content-center pb-5"
          css={{ height: '13rem' }}
        >
          <ODSSpinner />
        </div>
      </ODSLoginPortal>
    );
  }

  return (
    <SmartLoginContextProvider
      withProfile={passedInProfile}
      authQueryString={authQueryString}
      allowedSchemas={allowedSchemas}
      showCuiDisclaimer={showCuiDisclaimer}
      accountLinkingEnabled={accountLinkingEnabled}
      accountValidation={oidcProfileToAccountValidation(oidcProfile) ?? accountValidation}
    >
      <LoginPortalWithSSOBanner>
        <SmartLoginForm />
      </LoginPortalWithSSOBanner>
    </SmartLoginContextProvider>
  );
}

export default Login;
